import React from "react";
import formatCustomDate from "../helpers/customDateFormat";

const parseDenominationBreakdown = (entry) => {
  const breakdown = entry?.denomination_breakdown;
  if (breakdown && typeof breakdown === "object" && !Array.isArray(breakdown)) {
    return breakdown;
  }

  if (
    typeof entry?.denomination_amount === "string" &&
    entry.denomination_amount.trim()
  ) {
    try {
      const parsed = JSON.parse(entry.denomination_amount);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      return null;
    }
  }

  return null;
};

const formatCurrency = (value) =>
  `Rs.${(parseFloat(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const Receipt = React.forwardRef((props, ref) => {
  const { eventDetails, items } = props;

  const customerName = eventDetails?.customer_name || "-";
  const customerMobile = eventDetails?.customer_mobile || "";
  const eventType = eventDetails?.event_type || "-";
  const eventDate = formatCustomDate(eventDetails?.event_date, "date") || "-";
  const eventAddress = eventDetails?.event_address || "-";
  const eventor1 = eventDetails?.eventor_1 || "";
  const eventor2 = eventDetails?.eventor_2 || "";
  const eventors = [eventor1, eventor2].filter(Boolean).join(", ");
  const operatorName = eventDetails?.operator_name || "-";
  const vendorName = eventDetails?.vendor_name || "Digital Moi Tech";
  const vendorPhone = eventDetails?.vendor_mobile || "";
  const vendorAddress = eventDetails?.vendor_address || "";

  const name = items?.name || "-";
  const familyName = items?.family_name || "-";
  const occupation = items?.occupation || "-";
  const mobile = items?.mobile || "-";
  const village = items?.village || "-";
  const area = items?.area || "";
  const city = items?.city || "-";
  const assetType = items?.asset_type || "-";
  const amountValue = parseFloat(items?.amount) || 0;
  const asset = items?.asset || "-";
  const receiptNumber = items?.receipt_no || items?.token || items?.entry_id || items?.id || "-";

  const denominationBreakdown = parseDenominationBreakdown(items);
  const denomEntries = denominationBreakdown
    ? Object.entries(denominationBreakdown)
        .map(([note, count]) => ({
          note: parseInt(note, 10) || 0,
          count: parseInt(count, 10) || 0,
        }))
        .filter((entry) => entry.note > 0 && entry.count > 0)
        .sort((a, b) => b.note - a.note)
    : [];

  const hasDenomination = denomEntries.length > 0;
  const denominationTotal = denomEntries.reduce(
    (sum, entry) => sum + entry.note * entry.count,
    0,
  );
  const differenceAmount = denominationTotal - amountValue;
  const isCash = assetType === "cash";

  const printedAt = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div
      ref={ref}
      className="bg-white max-w-[80mm] mx-auto p-1 font-sans text-[11px] leading-tight text-black font-normal"
    >
      {/* VENDOR HEADER */}
      <div className="text-center border-b border-black pb-1 mb-1">
        <h1 className="text-[14px] font-medium uppercase tracking-wider m-0">{vendorName}</h1>
        <p className="text-[14px] mt-0.5">{[vendorAddress, vendorPhone].filter(Boolean).join(" | ")}</p>
        <p className="uppercase text-[14px] tracking-widest mt-0.5 font-medium">Moi Receipt</p>
        {receiptNumber !== "-" && <p className="text-[14px] font-bold mt-0.5">Receipt No: {receiptNumber}</p>}
      </div>

      {/* EVENT INFO (Highlighted) */}
      <div className="border border-black rounded p-1 mb-1 text-center">
        <h2 className="text-[14px] m-0 font-medium">{customerName}</h2>
        {customerMobile && <p className="text-[14px] mt-0.5">{customerMobile}</p>}
        <div className="h-px bg-black my-0.5 opacity-50"></div>
        <p className="text-[11px] uppercase font-medium">{eventType}</p>
        {eventors && <p className="text-[14px] mt-0.5">{eventors}</p>}
        <p className="text-[14px] mt-0.5">{eventDate}</p>
        {eventAddress && <p className="text-[14px] mt-0.5">{eventAddress}</p>}
      </div>

      {/* DONOR DETAILS */}
      <div className="mb-1">
        <table className="w-full text-left text-[11px]">
          <tbody>
            <tr className="text-[18px]">
              <td className="w-14 py-0.5 align-top whitespace-nowrap text-[15px] font-medium">பெயர்:</td>
              <td className="py-0.5 text-right text-[18px] font-bold">{name}</td>
            </tr>
            <tr><td className="w-14 py-0.5 align-top">வகைரா:</td><td className="py-0.5 text-right">{familyName}</td></tr>
            <tr><td className="w-14 py-0.5 align-top">தொழில்:</td><td className="py-0.5 text-right">{occupation}</td></tr>
            <tr><td className="w-14 py-0.5 align-top">செல்:</td><td className="py-0.5 text-right">{mobile}</td></tr>
            <tr><td className="w-14 py-0.5 align-top">ஊர்:</td><td className="py-0.5 text-right">{[village, area, city].filter((v) => v && v !== "-").join(", ") || "-"}</td></tr>
            <tr><td className="w-14 py-0.5 align-top">வகை:</td><td className="py-0.5 text-right">{assetType}</td></tr>
          </tbody>
        </table>
        
        {/* TOTAL HIGHLIGHT */}
        <div className="flex justify-between items-center border-y border-black py-1 mt-1 text-[14px] font-medium">
          <span>RECEIVED:</span>
          <span>{amountValue > 0 ? formatCurrency(amountValue) : asset}</span>
        </div>
      </div>

      {/* DENOMINATION */}
      {isCash && hasDenomination && (
        <div className="mb-1 border-b border-black pb-1">
          <div className="text-center border-b border-black mb-1 pb-0.5 uppercase tracking-wide text-[14px]">Denomination Breakdown</div>
          <table className="w-full text-[14px]">
            <tbody>
              {denomEntries.map(({ note, count }) => (
                <tr key={note}>
                  <td className="font-mono py-0.5 text-left">{note}</td>
                  <td className="font-mono py-0.5 text-center">x {count}</td>
                  <td className="font-mono py-0.5 text-right">{note * count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between border-t border-dashed border-black pt-1 mt-1 text-[11px] font-medium">
            <span>TOTAL:</span>
            <span>{formatCurrency(denominationTotal)}</span>
          </div>
        </div>
      )}

      {/* MOI RETURN/SHORT */}
      {isCash && hasDenomination && denominationTotal !== amountValue && (
        <div className="mb-1 border border-black rounded p-1 text-[14px]">
          <div className="flex justify-between mb-0.5"><span>MOI TOTAL:</span><span>{formatCurrency(amountValue)}</span></div>
          <div className="flex justify-between border-t border-dashed border-gray-400 pt-0.5 font-medium">
            <span>{differenceAmount > 0 ? "RETURN:" : "SHORT:"}</span>
            <span>{formatCurrency(Math.abs(differenceAmount))}</span>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div className="text-center mt-1 border-t border-dotted border-black pt-1 pb-1">
        <p className="italic text-[11px]">Thank You!</p>
        <p className="text-[9px] mt-0.5 uppercase tracking-wide">Op: {operatorName} | {printedAt}</p>
        <p className="text-[7px] text-gray-600 mt-1 uppercase tracking-widest">Powered by Digital Moi Tech</p>
      </div>
    </div>
  );
});

export default Receipt;
